import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Bouton, Champ, Saisie } from '../components/ui/Base'
import { Bandeau, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Cabinet as TypeCabinet } from '../lib/api/types'
import { minutesDepuisMinuit } from '../lib/temps'

const schema = z
  .object({
    nom: z.string().min(1, 'Le nom du cabinet est obligatoire.'),
    adresse: z.string().min(1, 'L’adresse est obligatoire.'),
    code_postal: z.string().regex(/^\d{5}$/, 'Le code postal comporte 5 chiffres.'),
    ville: z.string().min(1, 'La ville est obligatoire.'),
    siret: z.string().regex(/^\d{14}$/, 'Le SIRET comporte 14 chiffres.').or(z.literal('')),
    heure_ouverture: z.string().regex(/^\d{2}:\d{2}$/, 'Format attendu : HH:MM.'),
    heure_fermeture: z.string().regex(/^\d{2}:\d{2}$/, 'Format attendu : HH:MM.'),
    effectif_minimum: z.coerce.number().int().min(0, 'L’effectif ne peut pas être négatif.'),
    taux_horaire_defaut: z.coerce.number().min(0),
    indemnite_deplacement: z.coerce.number().min(0),
    tarif_kilometrique: z.coerce.number().min(0),
  })
  .refine((v) => minutesDepuisMinuit(v.heure_fermeture) > minutesDepuisMinuit(v.heure_ouverture), {
    message: 'L’heure de fermeture doit suivre l’heure d’ouverture.',
    path: ['heure_fermeture'],
  })

type Formulaire = z.infer<typeof schema>

/**
 * Réglages du cabinet — `TITULAIRE` uniquement (R6).
 *
 * L'amplitude saisie ici pilote les bornes de la grille de planning et le pas
 * de calcul de la couverture : un changement invalide donc les requêtes de
 * planning, sinon la grille garderait ses anciennes bornes jusqu'au prochain
 * rechargement complet.
 */
export function Cabinet() {
  const queryClient = useQueryClient()
  const toast = useToast()

  const requete = useQuery({
    queryKey: cles.cabinet,
    queryFn: ({ signal }) => api.get<TypeCabinet>('/cabinet', signal),
  })

  const { register, handleSubmit, reset, formState } = useForm<Formulaire>({
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (!requete.data) return
    const c = requete.data
    reset({
      nom: c.nom,
      adresse: c.adresse,
      code_postal: c.code_postal,
      ville: c.ville,
      siret: c.siret ?? '',
      heure_ouverture: c.heure_ouverture.slice(0, 5),
      heure_fermeture: c.heure_fermeture.slice(0, 5),
      effectif_minimum: c.effectif_minimum,
      taux_horaire_defaut: Number(c.taux_horaire_defaut),
      indemnite_deplacement: Number(c.indemnite_deplacement),
      tarif_kilometrique: Number(c.tarif_kilometrique),
    })
  }, [requete.data, reset])

  const enregistrer = useMutation({
    mutationFn: (valeurs: Formulaire) => api.patch<TypeCabinet>('/cabinet', valeurs),
    onSuccess: () => {
      toast('Réglages enregistrés.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.cabinet })
      // L'amplitude a peut-être changé : le planning doit se recalculer.
      void queryClient.invalidateQueries({ queryKey: cles.planning.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Enregistrement impossible.', 'erreur'),
  })

  if (requete.isPending) return <Squelette lignes={10} />
  if (requete.isError) return <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Réglages du cabinet</h1>
        <p className="text-sm text-slate-600">Identité, amplitude horaire et paramètres de facturation</p>
      </div>

      <form
        onSubmit={handleSubmit((valeurs) => enregistrer.mutate(valeurs))}
        className="space-y-6 rounded-lg border border-slate-200 bg-white p-5"
        noValidate
      >
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identité</h2>
          <Champ libelle="Nom du cabinet" id="nom" erreur={formState.errors.nom?.message} obligatoire>
            <Saisie id="nom" {...register('nom')} />
          </Champ>
          <Champ libelle="Adresse" id="adresse" erreur={formState.errors.adresse?.message} obligatoire>
            <Saisie id="adresse" {...register('adresse')} />
          </Champ>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Code postal" id="cp" erreur={formState.errors.code_postal?.message} obligatoire>
              <Saisie id="cp" inputMode="numeric" {...register('code_postal')} />
            </Champ>
            <Champ libelle="Ville" id="ville" erreur={formState.errors.ville?.message} obligatoire>
              <Saisie id="ville" {...register('ville')} />
            </Champ>
            <Champ libelle="SIRET" id="siret" erreur={formState.errors.siret?.message} aide="14 chiffres">
              <Saisie id="siret" inputMode="numeric" {...register('siret')} />
            </Champ>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Amplitude et effectif
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Ouverture" id="ouverture" erreur={formState.errors.heure_ouverture?.message} obligatoire>
              <Saisie id="ouverture" type="time" {...register('heure_ouverture')} />
            </Champ>
            <Champ libelle="Fermeture" id="fermeture" erreur={formState.errors.heure_fermeture?.message} obligatoire>
              <Saisie id="fermeture" type="time" {...register('heure_fermeture')} />
            </Champ>
            <Champ
              libelle="Effectif minimum"
              id="effectif"
              erreur={formState.errors.effectif_minimum?.message}
              aide="Seuil d’alerte de couverture"
            >
              <Saisie id="effectif" type="number" min={0} {...register('effectif_minimum')} />
            </Champ>
          </div>
          <Bandeau ton="info">
            L&apos;amplitude pilote les bornes de la grille de planning. Un cabinet qui commence à
            6 h et finit à 21 h la voit entièrement ; la réduire masque les créneaux extrêmes.
          </Bandeau>
        </section>

        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Honoraires</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Taux horaire par défaut (€/h)" id="taux" erreur={formState.errors.taux_horaire_defaut?.message}>
              <Saisie id="taux" type="number" step="0.01" min={0} {...register('taux_horaire_defaut')} />
            </Champ>
            <Champ
              libelle="Indemnité de déplacement (€)"
              id="ifd"
              aide="IFD, par intervention"
              erreur={formState.errors.indemnite_deplacement?.message}
            >
              <Saisie id="ifd" type="number" step="0.01" min={0} {...register('indemnite_deplacement')} />
            </Champ>
            <Champ
              libelle="Tarif kilométrique (€/km)"
              id="ik"
              aide="IK"
              erreur={formState.errors.tarif_kilometrique?.message}
            >
              <Saisie id="ik" type="number" step="0.01" min={0} {...register('tarif_kilometrique')} />
            </Champ>
          </div>
        </section>

        <div className="flex justify-end">
          <Bouton type="submit" disabled={enregistrer.isPending}>
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      </form>
    </div>
  )
}
